import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import TextFieldsIcon from "@material-ui/icons/TextFields";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Can } from "../../components/Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import CampaignModalPhrase from "../../components/CampaignModalPhrase";
import api from "../../services/api";
import MetricsStrip from "../../components/MetricsStrip";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  tableSearch: {
    width: 320,
    maxWidth: "100%",
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    fontWeight: 600,
  },
  nameCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 220,
  },
}));

const getStatusMeta = (status) => {
  if (status) {
    return {
      label: "Ativo",
      backgroundColor: "#dcfce7",
      color: "#166534",
    };
  }

  return {
    label: "Desativado",
    backgroundColor: "#e5e7eb",
    color: "#374151",
  };
};

const CampaignsPhrase = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [searchParam, setSearchParam] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [campaignFlows, setCampaignFlows] = useState([]);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);

  const fetchCampaignFlows = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/flowcampaign");
      setCampaignFlows(data.flow || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignFlows();
  }, []);

  const filteredFlows = useMemo(() => {
    const normalizedSearch = searchParam.trim().toLowerCase();

    if (!normalizedSearch) {
      return campaignFlows;
    }

    return campaignFlows.filter((flow) => {
      const name = flow?.name?.toLowerCase() || "";
      const status = flow?.status ? "ativo" : "desativado";
      return name.includes(normalizedSearch) || status.includes(normalizedSearch);
    });
  }, [campaignFlows, searchParam]);

  const summary = useMemo(() => {
    const active = filteredFlows.filter((flow) => flow.status).length;

    return {
      loaded: campaignFlows.length,
      visible: filteredFlows.length,
      active,
      inactive: filteredFlows.length - active,
    };
  }, [campaignFlows, filteredFlows]);

  const summaryCards = useMemo(
    () => [
      { key: "loaded", label: "Carregados", value: summary.loaded },
      { key: "visible", label: "Visiveis", value: summary.visible },
      { key: "active", label: "Ativos", value: summary.active },
      { key: "inactive", label: "Desativados", value: summary.inactive },
    ],
    [summary]
  );

  const handleDeleteFlow = async (flowId) => {
    try {
      await api.delete(`/flowcampaign/${flowId}`);
      toast.success("Frase deletada");
      await fetchCampaignFlows();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingFlow(null);
      setConfirmModalOpen(false);
    }
  };

  const handleOpenCreateModal = () => {
    setSelectedFlowId(null);
    setFlowModalOpen(true);
  };

  const handleOpenEditModal = (flowId) => {
    setSelectedFlowId(flowId);
    setFlowModalOpen(true);
  };

  return (
    <MainContainer className={classes.mainContainer} fullWidth>
      <ConfirmationModal
        title={deletingFlow ? `Excluir ${deletingFlow.name}?` : ""}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteFlow(deletingFlow.id)}
      >
        Esta acao nao pode ser desfeita.
      </ConfirmationModal>

      <CampaignModalPhrase
        open={flowModalOpen}
        onClose={() => setFlowModalOpen(false)}
        FlowCampaignId={selectedFlowId}
        onSave={fetchCampaignFlows}
      />

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              Fluxo de campanha
            </Typography>
          </Box>
          <Box className={classes.heroActions}>
            <Button variant="outlined" onClick={fetchCampaignFlows} disabled={loading}>
              Atualizar
            </Button>
            <Button variant="contained" color="primary" onClick={handleOpenCreateModal}>
              <Box display="inline-flex" alignItems="center" gridGap={8}>
                <AddCircleOutlineIcon fontSize="small" />
                Novo fluxo
              </Box>
            </Button>
          </Box>
        </Box>

        <MetricsStrip ariaLabel="Indicadores de fluxos de campanha" items={summaryCards} />

        <Paper className={classes.tablePaper} variant="outlined">
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Fluxos operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <TextField
                className={classes.tableSearch}
                variant="outlined"
                size="small"
                placeholder="Buscar fluxo de campanha"
                type="search"
                value={searchParam}
                onChange={(event) => setSearchParam(event.target.value)}
                inputProps={{ "aria-label": "Pesquisar fluxos de campanha" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip size="small" label={`${summary.visible} visiveis`} />
              <Chip size="small" label={`${summary.active} ativos`} />
            </Box>
          </Box>

          {loading ? (
            <Box className={classes.loadingBox}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Nome</TableCell>
                    <TableCell align="center" className={classes.tableHeadCell}>
                      Status
                    </TableCell>
                    <TableCell align="right" className={classes.tableHeadCell}>
                      Acoes
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFlows.map((flow) => {
                    const statusMeta = getStatusMeta(flow.status);

                    return (
                      <TableRow key={flow.id} hover>
                        <TableCell>
                          <Box className={classes.nameCell}>
                            <TextFieldsIcon fontSize="small" />
                            <Typography variant="body2">{flow.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={statusMeta.label}
                            style={{
                              backgroundColor: statusMeta.backgroundColor,
                              color: statusMeta.color,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" className={classes.actionsCell}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditModal(flow.id)}
                          >
                            <EditIcon />
                          </IconButton>
                          <Can
                            role={user.profile}
                            perform="contacts-page:deleteContact"
                            yes={() => (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeletingFlow(flow);
                                  setConfirmModalOpen(true);
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && filteredFlows.length === 0 && (
            <Box p={4} textAlign="center">
              <Typography variant="h6">Nenhum fluxo encontrado</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Ajuste a busca ou cadastre um novo fluxo para continuar.
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default CampaignsPhrase;
